import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Map, CheckCircle2, Circle, ShieldCheck, LineChart, Layers,
  Landmark, Bot, GaugeCircle, Import, Rocket,
} from "lucide-react";

const LIVE = [
  { icon: LineChart, title: "Multi-asset paper engine", desc: "Stocks, crypto, futures (MES/MNQ/ES/NQ) and options — long calls/puts plus covered calls & cash-secured puts." },
  { icon: GaugeCircle, title: "Deterministic risk engine", desc: "Position caps, daily-loss limits, kill switches, market hours, duplicate protection on every order." },
  { icon: Layers, title: "Advanced order lifecycle", desc: "Limit, stop, take-profit, OCO brackets and trailing stops with background execution." },
  { icon: Bot, title: "AI coaching (Claude)", desc: "Per-trade grading, weekly coach digests, import batch insights and a 5-agent AI council." },
  { icon: Import, title: "Broker CSV import", desc: "TradeStation & Tradovate adapters plus generic auto-detect — journal-only, audit-safe." },
  { icon: Landmark, title: "Broker abstraction", desc: "Tradier, TradeStation and IBKR adapters scaffolded behind hard safety gates. Live routing stays OFF until UAT." },
];

const STAGES = [
  {
    n: 1, title: "Options paper MVP", status: "live",
    points: ["Single-leg long calls & puts on SPY and AAPL", "Live chain browser with full Greeks (delta, gamma, theta, vega)", "Premium accounting, expiry auto-settlement, AI grading"],
  },
  {
    n: 2, title: "Income strategies", status: "live",
    points: ["Covered calls & cash-secured puts with enforced collateral rules", "Assignment simulation — shares transfer at the strike on ITM expiry", "AI options coach: premium risk, theta decay & moneyness habits"],
  },
  {
    n: 3, title: "Spreads & multi-leg", status: "next",
    points: ["Verticals first, then straddles / iron condors", "Atomic multi-leg orders", "Strategy-level P&L and defined-risk margin"],
  },
  {
    n: 4, title: "Live routing", status: "gated",
    points: ["Broker options-approval levels per user", "Real-time chains & Greeks", "Only after UAT, audit trail and risk-control sign-off"],
  },
];

const statusBadge = {
  live: <Badge className="rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-100">LIVE NOW</Badge>,
  next: <Badge className="rounded-full bg-brand-tint text-brand-deep hover:bg-brand-tint">UP NEXT</Badge>,
  planned: <Badge variant="outline" className="rounded-full text-gray-600">PLANNED</Badge>,
  gated: <Badge variant="outline" className="rounded-full border-amber-300 text-amber-700">SAFETY-GATED</Badge>,
};

export default function Roadmap() {
  return (
    <div className="space-y-10 max-w-5xl" data-testid="roadmap-root">
      <div>
        <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
          <Map size={24} /> Product roadmap
        </h1>
        <p className="text-sm text-gray-500 mt-2 max-w-2xl">
          TradePilot is built paper-first: every asset class is proven with full risk controls,
          AI coaching and journaling before any live-broker routing is considered.
          Futures shipped on this playbook — options are following the same path.
        </p>
      </div>

      <section data-testid="roadmap-live-section">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <CheckCircle2 size={17} className="text-emerald-600" /> What&apos;s live today
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LIVE.map(({ icon: Icon, title, desc }) => (
            <Card key={title} className="border border-gray-200 rounded-2xl p-4">
              <Icon size={18} className="text-brand mb-2" />
              <p className="text-sm font-semibold">{title}</p>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{desc}</p>
            </Card>
          ))}
        </div>
      </section>

      <section data-testid="roadmap-stages-section">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Rocket size={17} className="text-brand" /> Options rollout — staged &amp; paper-first
        </h2>
        <div className="space-y-4">
          {STAGES.map((s) => (
            <Card key={s.n} className="border border-gray-200 rounded-2xl p-5" data-testid={`roadmap-stage-${s.n}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    s.status === "live" ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-600"
                  }`}>
                    {s.n}
                  </span>
                  <p className="font-semibold">{s.title}</p>
                </div>
                {statusBadge[s.status]}
              </div>
              <ul className="mt-3 ml-11 space-y-1.5">
                {s.points.map((p) => (
                  <li key={p} className="text-sm text-gray-600 flex items-start gap-2">
                    <Circle size={5} className="mt-1.5 text-brand shrink-0 fill-current" /> {p}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </section>

      <section data-testid="roadmap-safety-section">
        <Card className="border border-amber-200 bg-amber-50/50 rounded-2xl p-5">
          <h2 className="text-base font-semibold flex items-center gap-2 text-amber-800">
            <ShieldCheck size={17} /> Our safety commitment
          </h2>
          <p className="text-sm text-amber-900/80 mt-2 leading-relaxed">
            Live trading is hard-disabled at the platform level and stays off until broker credentials,
            user acceptance testing, immutable audit logs, kill switches and risk controls are fully
            validated. Paper performance never touches real money, and options market data on the free
            tier is delayed — suitable for learning and strategy validation, not live execution decisions.
          </p>
        </Card>
      </section>
    </div>
  );
}
